import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { Editor } from "@tiptap/core";
import { AnimatePresence } from "framer-motion";
import { defer, keyBy, last, throttle } from "lodash";
import {
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  CancelAskAiMessageMutationMutation,
  CancelAskAiMessageMutationMutationVariables,
  ChatbotRoleEnum,
  CreateAskAiMessageMutationMutation,
  CreateAskAiMessageMutationMutationVariables,
  GetAskAiChatSessionQueryQuery,
  GetAskAiChatSessionQueryQueryVariables,
  RetryAskAiMessageMutationMutation,
  RetryAskAiMessageMutationMutationVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import getAskAiChatSessionHistoryQuery from "@apps/ask-ai/graphql/get-ask-ai-chat-session-history-query";
import useLabel from "@apps/use-label/use-label";
import cache from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { getWatchedQuery } from "@graphql/client";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
} from "@helpers/helpers";

import {
  ChatSessionContext,
  chatSessionLoadingTextVar,
  chatToolCallsVar,
  pendingChatPromptVar,
} from "./ask-ai-context";
import { AskAIAttachment } from "./attachments";
import AskAIErrorMessage from "./components/ask-ai-error-message";
import AskAIForm from "./components/ask-ai-form";
import AskAILoading from "./components/ask-ai-loading";
import AskAIMessage from "./components/ask-ai-message";
import AskAIPromptSuggestions from "./components/ask-ai-prompt-suggestions";
import AskAIToolCalls from "./components/ask-ai-tool-calls";
import CoordinatorSimulationBanner from "./components/coordinator-simulation-banner";
import cancelAskAiMessageMutation from "./graphql/cancel-ask-ai-message-mutation";
import createAskAiMessageMutation from "./graphql/create-ask-ai-message-mutation";
import getAskAIChatSessionQuery from "./graphql/get-ask-ai-session-query";
import retryAskAiMessageMutation from "./graphql/retry-ask-ai-message-mutation";
import { buildInitialEditorContent, createChatMessageNode } from "./helpers";
import {
  getAskAIResponseStatus,
  shouldShowStopButton,
} from "./response-status";

export type ChatSessionFunctionCallType = {
  chatSessionId: number;
  functionName: string;
  parameters?: any;
} | null;

export type ChatSessionContextValue = {
  assessmentId?: number;
  assessmentQuestionId?: number;
  meetingId?: number;
  currentSuggestedAssessmentResponseId?: string | null;
  setCurrentSuggestedAssessmentResponseId?: (id: string | null) => void;
};

const AskAI = ({
  chatSessionId,
  onChangeChatSessionId,
  onStartChatSession,
  className,
  isFullScreen,
  totalYPaddingAroundContainer,
  isInSidebar = false,
  initialPrompt,
  autoSubmitInitialPrompt = false,
  pendingPromptTarget,
  showChatHistory = false,
  isIncognito = false,
  showPromptSuggestions = false,
  collapsibleInput = false,
}: {
  chatSessionId: number | null;
  onChangeChatSessionId: (chatSessionId: number | null) => void;
  onStartChatSession: () => void;
  className?: string;
  isFullScreen: boolean;
  totalYPaddingAroundContainer: number;
  isInSidebar?: boolean;
  /** Pre-fills the input. May lead with a skill reference `/slug[:skillId]`
   * (e.g. from "Test in Topicflow AI"), which becomes a skill chip so the exact
   * skill runs even when a same-command personal skill would win by precedence;
   * any trailing text follows the chip. See buildInitialEditorContent. */
  initialPrompt?: string;
  /** When true, `initialPrompt` is sent automatically as soon as the editor is
   * ready, so the conversation starts without the user pressing send. Used by
   * flows that launch a specific skill (e.g. report onboarding). Leave false to
   * only pre-fill (e.g. "Test in Topicflow AI", where the dev reviews first). */
  autoSubmitInitialPrompt?: boolean;
  /** Also consume pendingChatPromptVar prompts whose `target` matches (for
   * AskAIs not scoped to a meeting). */
  pendingPromptTarget?: string;
  showChatHistory?: boolean;
  isIncognito?: boolean;
  /** Show starter prompts under the input (homepage only, TF-1590). They are
   * hidden as soon as a session exists, so they never sit under a conversation. */
  showPromptSuggestions?: boolean;
  /** Render the input as a single line that grows on focus (TF-1590). Only
   * applies before a conversation starts; the full-screen form is never
   * collapsed. */
  collapsibleInput?: boolean;
}) => {
  const chatSessionLoadingText = useReactiveVar(chatSessionLoadingTextVar);
  const chatToolCalls = useReactiveVar(chatToolCallsVar);
  const pendingChatPrompt = useReactiveVar(pendingChatPromptVar);

  const location = useLocation<TFLocationState>();

  const lastUserMessageRef = useRef<null | HTMLDivElement>(null);
  const editorRef = useRef<null | Editor>(null);
  const scrollRef = useRef<null | HTMLDivElement>(null);
  const {
    assessmentId,
    assessmentQuestionId,
    meetingId,
    currentSuggestedAssessmentResponseId,
  } = useContext(ChatSessionContext);

  const [inputMessage, setInputMessage] = useState("");
  const [retryCount, setRetryCount] = useState(0); // used to force re-render the loading/error UI
  const [isRetryingError, setIsRetryingError] = useState(false);
  const [
    inputMessageWhileCreatingSession,
    setInputMessageWhileCreatingSession,
  ] = useState("");
  const [imageUrlsWhileCreatingSession, setImageUrlsWhileCreatingSession] =
    useState<string[]>([]);
  const [documentsWhileCreatingSession, setDocumentsWhileCreatingSession] =
    useState<
      { __typename: "ChatDocumentNode"; url: string; filename: string }[]
    >([]);
  const [attachments, setAttachments] = useState<AskAIAttachment[]>([]);
  const hasUsedSystemContextMessageRef = useRef(false);
  const pendingSystemContextMessageRef = useRef(false);
  const initialPromptAppliedRef = useRef(false);

  const [
    retryAskAiMessage,
    { loading: loadingRetryAskAiMessage, called: calledRetryAskAiMessage },
  ] = useMutation<
    RetryAskAiMessageMutationMutation,
    RetryAskAiMessageMutationMutationVariables
  >(retryAskAiMessageMutation);

  const [
    createAskAIMessage,
    {
      loading: loadingCreateMessage,
      error: errorCreateMessage,
      called: calledCreateAskAiMessage,
    },
  ] = useMutation<
    CreateAskAiMessageMutationMutation,
    CreateAskAiMessageMutationMutationVariables
  >(createAskAiMessageMutation);

  const [cancelAskAIMessage] = useMutation<
    CancelAskAiMessageMutationMutation,
    CancelAskAiMessageMutationMutationVariables
  >(cancelAskAiMessageMutation);

  // Keep the loading and streaming UI suppressed even when Pusher creates the
  // assistant message after Stop (TF-1512). Clear it before the next response.
  const [isResponseStopped, setIsResponseStopped] = useState(false);

  const hasPostedMessage = calledCreateAskAiMessage || calledRetryAskAiMessage;

  const { data, loading } = useQuery<
    GetAskAiChatSessionQueryQuery,
    GetAskAiChatSessionQueryQueryVariables
  >(getAskAIChatSessionQuery, {
    variables: { chatSessionId: chatSessionId || 0 },
    skip: !chatSessionId,
    fetchPolicy: "cache-and-network",
  });
  const chatSession = data?.chatSession;

  // The suggested-answer card is only offered when the chat is tied to an
  // assessment/review (opened from a review question, or a session linked to one).
  const isAssessmentSession = Boolean(
    assessmentId ||
      assessmentQuestionId ||
      chatSession?.assessment?.id ||
      chatSession?.assessmentQuestion?.id,
  );

  const systemContextMessage = useMemo(() => {
    if (!currentSuggestedAssessmentResponseId) {
      return undefined;
    }
    return `User is currently looking at suggested response with id \`${currentSuggestedAssessmentResponseId}\``;
  }, [currentSuggestedAssessmentResponseId]);

  useEffect(() => {
    hasUsedSystemContextMessageRef.current = false;
    pendingSystemContextMessageRef.current = false;
    setIsResponseStopped(false);
  }, [chatSessionId]);

  const messages = useMemo(
    () =>
      !chatSession &&
      (inputMessageWhileCreatingSession ||
        imageUrlsWhileCreatingSession.length ||
        documentsWhileCreatingSession.length)
        ? [
            createChatMessageNode({
              content: inputMessageWhileCreatingSession,
              imageUrls: imageUrlsWhileCreatingSession,
              documents: documentsWhileCreatingSession,
            }),
          ]
        : chatSession?.messages
          ? assertEdgesNonNullWithStringId(chatSession.messages)
          : [],
    [
      chatSession,
      inputMessageWhileCreatingSession,
      imageUrlsWhileCreatingSession,
      documentsWhileCreatingSession,
    ],
  );

  const lastMessage = last(messages);

  const sendMessage = useCallback(
    (
      content: string,
      imageUrls: string[],
      attachmentsToRestore: AskAIAttachment[] = [],
    ) => {
      const documents = attachmentsToRestore
        .filter((attachment) => !attachment.isImage && attachment.url)
        .map((attachment) => ({
          __typename: "ChatDocumentNode" as const,
          url: attachment.url!,
          filename: attachment.filename,
        }));
      const newMessageNode = createChatMessageNode({
        content,
        imageUrls,
        documents,
      });

      // If no chat session, set the fake input message while creating session
      if (!chatSession) {
        setInputMessageWhileCreatingSession(content);
        setImageUrlsWhileCreatingSession(imageUrls);
        setDocumentsWhileCreatingSession(documents);
      }
      const shouldIncludeSystemContextMessage =
        !!systemContextMessage &&
        !hasUsedSystemContextMessageRef.current &&
        !pendingSystemContextMessageRef.current;

      if (shouldIncludeSystemContextMessage) {
        pendingSystemContextMessageRef.current = true;
      }

      // If the input leads with a skill chip, send its exact id so the backend
      // runs that skill instead of re-parsing the `/slug` from the text.
      let skillId: string | null = null;
      editorRef.current?.state.doc.descendants((node) => {
        if (!skillId && node.type.name === "skillMention") {
          skillId = (node.attrs.skillId as string) ?? null;
        }
      });

      createAskAIMessage({
        variables: {
          chatSessionId: chatSession?.id,
          meetingId,
          assessmentId,
          assessmentQuestionId,
          message: content,
          imageUrls,
          documentUrls: documents.map((document) => document.url),
          isIncognito,
          skillId,
          systemContextMessage: shouldIncludeSystemContextMessage
            ? systemContextMessage
            : undefined,
        },
        optimisticResponse: chatSession
          ? {
              __typename: "Mutation",
              sendMessageToChatbot: {
                __typename: "SendMessageToChatbotMutation",
                chatSession: {
                  ...chatSession,
                  updated: new Date().toISOString(),
                  messages: {
                    __typename: "ChatMessageConnection",
                    edges: [
                      ...chatSession.messages.edges,
                      {
                        __typename: "ChatMessageEdge",
                        node: newMessageNode,
                      },
                    ],
                  },
                },
              },
            }
          : undefined,
        onCompleted: (response) => {
          attachmentsToRestore.forEach((attachment) =>
            URL.revokeObjectURL(attachment.previewUrl),
          );
          if (shouldIncludeSystemContextMessage) {
            hasUsedSystemContextMessageRef.current = true;
            pendingSystemContextMessageRef.current = false;
          }
          const newChatSession = response?.sendMessageToChatbot?.chatSession;
          if (!chatSession && newChatSession) {
            // Update cache for chat session and messages
            // so when this component is re-rendered with chatSessionId
            // it renders right away the messages
            cache.writeQuery<
              GetAskAiChatSessionQueryQuery,
              GetAskAiChatSessionQueryQueryVariables
            >({
              query: getAskAIChatSessionQuery,
              variables: { chatSessionId: newChatSession.id },
              data: {
                __typename: "Query",
                chatSession: newChatSession,
              },
            });

            // Update chat session id
            onChangeChatSessionId(newChatSession.id || null);

            // Update chat history
            if (!isIncognito) {
              const query = getWatchedQuery(getAskAiChatSessionHistoryQuery);
              if (query) query.refetch();
            }
          }
        },
        onError: () => {
          if (shouldIncludeSystemContextMessage) {
            pendingSystemContextMessageRef.current = false;
          }
          setInputMessage(content);
          setAttachments(attachmentsToRestore);
          setImageUrlsWhileCreatingSession([]);
          setDocumentsWhileCreatingSession([]);
          defer(() => {
            editorRef.current?.commands.setContent(content, {
              emitUpdate: false,
            });
          });
        },
      });
    },
    [
      chatSession,
      meetingId,
      assessmentId,
      assessmentQuestionId,
      onChangeChatSessionId,
      createAskAIMessage,
      systemContextMessage,
      isIncognito,
    ],
  );

  const clearInput = () => {
    setInputMessage("");
    defer(() => {
      editorRef.current?.commands.setContent("", { emitUpdate: false });
    });
  };

  const handleSubmitMessage = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();
      if (loadingCreateMessage || loadingRetryAskAiMessage) return;
      const imageUrls = attachments
        .filter((attachment) => attachment.isImage)
        .map((attachment) => attachment.url)
        .filter((imageUrl): imageUrl is string => !!imageUrl);
      const isUploadingFiles = attachments.some(
        (attachment) => attachment.isUploading,
      );
      if (
        isUploadingFiles ||
        (inputMessage.trim().length === 0 &&
          !attachments.some((attachment) => attachment.url))
      ) {
        return;
      }
      onStartChatSession();
      chatToolCallsVar([]);
      setIsResponseStopped(false);
      sendMessage(inputMessage, imageUrls, attachments);
      clearInput();
      setAttachments([]);
    },
    [
      inputMessage,
      attachments,
      loadingCreateMessage,
      loadingRetryAskAiMessage,
      onStartChatSession,
      sendMessage,
    ],
  );

  const handleChangeInputValue = useCallback(
    (value: string) => {
      setInputMessage(value);
    },
    [setInputMessage],
  );

  const handleEditorReady = useCallback(
    (editor: Editor) => {
      editorRef.current = editor;
      if (initialPrompt && !initialPromptAppliedRef.current) {
        initialPromptAppliedRef.current = true;
        defer(() => {
          // Pre-fill from `?p=`. A leading `/slug[:skillId]` becomes a real
          // skill chip (pill) — raw `/slug` text would trip the slash-command
          // popup and bind no specific skill — with any trailing text kept. The
          // chip carries the id, so the right skill runs.
          editor.commands.setContent(buildInitialEditorContent(initialPrompt), {
            emitUpdate: false,
          });
          const text = editor.getText();
          if (autoSubmitInitialPrompt && text.trim().length > 0) {
            // Auto-start flow (e.g. report onboarding): send immediately. The
            // chip is now in the editor, so sendMessage reads its skillId
            // synchronously and runs the right skill (same path as pressing
            // send). We deliberately do NOT sync inputMessage and we clear the
            // editor right after sending, so the prompt never lingers in the
            // input — it appears only as the user's chat message.
            onStartChatSession();
            chatToolCallsVar([]);
            sendMessage(text, []);
            editor.commands.setContent("", { emitUpdate: false });
          } else {
            // Manual flow: keep the prompt in the input (the chip serializes
            // back to `/slug`) so the user can review, edit, and send it.
            editor.commands.focus("end");
            setInputMessage(text);
          }
        });
      }
    },
    [initialPrompt, autoSubmitInitialPrompt, onStartChatSession, sendMessage],
  );

  const handleClickPromptSuggestion = useCallback(
    (prompt: string) => {
      onStartChatSession();
      chatToolCallsVar([]);
      setIsResponseStopped(false);
      sendMessage(prompt, []);
    },
    [sendMessage, onStartChatSession],
  );

  // Consume a pending auto-submit prompt set by callers outside AskAI (e.g.
  // the "Prepare with Topicflow AI" card on an empty 1-on-1). We only act on
  // prompts targeted at this AskAI's meeting and clear the var so it doesn't
  // re-fire on subsequent renders or revisits.
  useEffect(() => {
    if (
      pendingChatPrompt &&
      ((meetingId !== undefined && pendingChatPrompt.meetingId === meetingId) ||
        (pendingPromptTarget !== undefined &&
          pendingChatPrompt.target === pendingPromptTarget))
    ) {
      pendingChatPromptVar(null);
      handleClickPromptSuggestion(pendingChatPrompt.prompt);
    }
  }, [
    pendingChatPrompt,
    meetingId,
    pendingPromptTarget,
    handleClickPromptSuggestion,
  ]);

  const filteredMessages = useMemo(() => {
    return (
      messages.filter(
        ({ autogenerated, role, id, error }) =>
          !autogenerated &&
          role !== ChatbotRoleEnum.System &&
          // only show error message if it is the last message
          (!error || (error && id === lastMessage?.id)),
      ) || []
    );
  }, [messages, lastMessage]);

  const messageLength = useMemo(
    () => filteredMessages.length,
    [filteredMessages],
  );
  const hasMessages = useMemo(
    () => filteredMessages.length > 0,
    [filteredMessages],
  );

  const hasNoMessages = useMemo(
    () => filteredMessages.length === 0,
    [filteredMessages],
  );

  const lastMessageIsFromAssistant = useMemo(
    () => !!lastMessage && lastMessage.role === ChatbotRoleEnum.Assistant,
    [lastMessage],
  );
  const lastMessageIsFromUser = useMemo(
    () => !!lastMessage && lastMessage.role === ChatbotRoleEnum.User,
    [lastMessage],
  );
  const lastMessageIsError = useMemo(
    () => !!lastMessage && !!lastMessage.error,
    [lastMessage],
  );
  // The backend streams a single assistant message across a multi-step agent
  // loop (text -> tool call -> more text). Between those steps there can be
  // gaps where no loading text or in-flight tool call is signalled, which made
  // a partial answer look finished and then "resume typing". Treat a streaming
  // (not yet finalized) assistant message as still in progress so we always
  // show a working indicator until new_chat_message flips isStreaming off.
  const lastMessageIsStreaming = useMemo(
    () =>
      !!lastMessage &&
      lastMessage.role === ChatbotRoleEnum.Assistant &&
      !!lastMessage.isStreaming,
    [lastMessage],
  );

  const lastMessageFromUser = useMemo(
    () =>
      filteredMessages.findLast(
        (message) => message.role === ChatbotRoleEnum.User,
      ),
    [filteredMessages],
  );

  const waitingForSuggestedAssessmentResponse = useMemo(() => {
    const hasOnlySystemMessages = messages.every(
      (message) => message.role === ChatbotRoleEnum.System,
    );
    const isForAssessmentQuestion =
      !!chatSession?.assessment && !!chatSession.assessmentQuestion;
    return hasOnlySystemMessages && isForAssessmentQuestion;
  }, [messages, chatSession]);

  const lastUpdate = useMemo(() => {
    return chatSession?.updated;
  }, [chatSession]);

  const showLoadingUI = useMemo(() => {
    return (
      waitingForSuggestedAssessmentResponse ||
      loadingCreateMessage ||
      loadingRetryAskAiMessage ||
      isRetryingError ||
      (!hasNoMessages && !lastMessageIsFromAssistant)
    );
  }, [
    waitingForSuggestedAssessmentResponse,
    loadingCreateMessage,
    loadingRetryAskAiMessage,
    isRetryingError,
    hasNoMessages,
    lastMessageIsFromAssistant,
  ]);

  const reactionsByMessageId = useMemo(() => {
    const reactions = data?.chatSession?.reactions
      ? assertEdgesNonNull(data.chatSession.reactions)
      : [];
    return keyBy(reactions, "messageId");
  }, [data]);

  const handleClickScrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current?.scrollHeight,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (
      lastMessage?.role === ChatbotRoleEnum.User &&
      lastUserMessageRef.current
    ) {
      lastUserMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [lastMessage]);

  // Scroll to bottom for assistant messages
  useEffect(() => {
    if (
      hasMessages &&
      lastMessageIsFromAssistant &&
      scrollRef.current &&
      !hasPostedMessage
    ) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [hasMessages]); // only scroll to bottom when when we are loading the messages

  const [canClickScrollToBottom, setCanClickScrollToBottom] = useState(false);

  // Debounce handleCheckIfCanClickScrollButton to avoid excessive calls on scroll
  const handleCheckIfCanClickScrollButton = useCallback(() => {
    const scrollTop = scrollRef.current?.scrollTop || 0;
    const scrollHeight = scrollRef.current?.scrollHeight || 0;
    const clientHeight = scrollRef.current?.clientHeight || 0;
    const canScrollToBottom = scrollTop + clientHeight < scrollHeight - 100;
    setCanClickScrollToBottom(canScrollToBottom);
  }, []);

  const throttleCheckIfCanClickScrollButton = useMemo(
    () => throttle(handleCheckIfCanClickScrollButton, 1000),
    [handleCheckIfCanClickScrollButton],
  );

  useEffect(() => {
    // Cleanup debounce on unmount
    return () => {
      throttleCheckIfCanClickScrollButton.cancel?.();
    };
  }, [throttleCheckIfCanClickScrollButton]);

  useEffect(() => {
    if (lastMessage?.role === ChatbotRoleEnum.Assistant) {
      throttleCheckIfCanClickScrollButton();
    }
  }, [lastMessage, handleCheckIfCanClickScrollButton]);

  useEffect(() => {
    const div = scrollRef.current;
    if (div) {
      div.addEventListener("scroll", throttleCheckIfCanClickScrollButton, {
        passive: true,
      });
      throttleCheckIfCanClickScrollButton();
    }
    return () => {
      if (div) {
        div.removeEventListener("scroll", throttleCheckIfCanClickScrollButton);
      }
    };
  }, [messageLength]);

  const handleReSendMessage = useCallback(() => {
    chatToolCallsVar([]);
    handleSubmitMessage();
    // we re-create the chat session if there is no chat session id
    // sendMessage(inputMessage);
    setRetryCount((val) => val + 1);
  }, [handleSubmitMessage]);

  const handleRetryLastMessage = useCallback(() => {
    // we re-create the chat session if there is no chat session id
    if (!chatSessionId) {
      handleReSendMessage();
    } else if (
      waitingForSuggestedAssessmentResponse ||
      lastMessageIsFromUser ||
      lastMessageIsError
    ) {
      if (lastMessageIsError) {
        setIsRetryingError(true);
      }
      chatToolCallsVar([]);
      setIsResponseStopped(false);
      retryAskAiMessage({ variables: { chatSessionId } });
      setRetryCount((val) => val + 1);
    }
  }, [
    waitingForSuggestedAssessmentResponse,
    lastMessageIsFromUser,
    lastMessageIsError,
    chatSessionId,
    retryAskAiMessage,
    handleReSendMessage,
  ]);

  useEffect(() => {
    setIsRetryingError(false);
  }, [lastMessage?.id]);

  // Clear input when the url changes (skip initial mount if there's an initial prompt)
  const isInitialMountRef = useRef(true);
  useEffect(() => {
    if (isInitialMountRef.current && initialPrompt) {
      isInitialMountRef.current = false;
      return;
    }
    isInitialMountRef.current = false;
    clearInput();
    setInputMessageWhileCreatingSession("");
    setImageUrlsWhileCreatingSession([]);
    setDocumentsWhileCreatingSession([]);
    setAttachments((attachments) => {
      attachments.forEach((attachment) =>
        URL.revokeObjectURL(attachment.previewUrl),
      );
      return [];
    });
  }, [location.search, location.pathname]);

  // Clear function call when the messages change
  useEffect(() => {
    chatSessionLoadingTextVar(null);
  }, [loading, messages.length]);

  const functionCallLabel: null | string = useMemo(() => {
    return chatSessionLoadingText?.text || null;
  }, [chatSessionLoadingText]);

  const liveToolCalls = useMemo(() => {
    return chatToolCalls
      .filter((tc) => tc.chatSessionId === chatSessionId)
      .map((tc) => ({
        toolName: tc.toolName,
        resultCount: tc.resultCount ?? null,
        description: tc.description,
        inFlight: tc.status === "started",
      }));
  }, [chatToolCalls, chatSessionId]);

  const hasInFlightToolCall = liveToolCalls.some((tc) => tc.inFlight);

  // The user stopped this turn, so suppress every loading/streaming affordance
  // until they start another response (TF-1512). Any partial answer already
  // streamed stays visible; only the working indicator disappears.
  const responseStatus = isResponseStopped
    ? "none"
    : getAskAIResponseStatus({
        showLoadingUI,
        functionCallLabel,
        lastMessageIsStreaming,
        lastMessageContent: lastMessage?.content,
        lastMessageIsFromAssistant,
        hasInFlightToolCall,
      });
  const isActivelyWaitingForResponse =
    !isResponseStopped &&
    (loadingCreateMessage ||
      loadingRetryAskAiMessage ||
      isRetryingError ||
      !!functionCallLabel ||
      hasInFlightToolCall ||
      lastMessageIsStreaming);
  const isResponding =
    !isResponseStopped &&
    (showLoadingUI || !!functionCallLabel || hasInFlightToolCall);
  const showStopButton = shouldShowStopButton(
    responseStatus,
    hasInFlightToolCall,
  );

  const handleStopResponse = useCallback(() => {
    setIsResponseStopped(true);
    // Clear the pusher-driven working indicators immediately so the UI unblocks
    // without waiting on the backend.
    chatSessionLoadingTextVar(null);
    chatToolCallsVar([]);
    // Best-effort: tell the backend to stop and persist its partial answer.
    if (chatSessionId) {
      cancelAskAIMessage({ variables: { chatSessionId } }).catch(() => {
        // Local stop already unblocked the UI; a failed cancel is harmless.
      });
    }
  }, [chatSessionId, cancelAskAIMessage]);

  const label = useLabel();
  const heightOfFormAndLastMessage = lastMessageIsFromAssistant
    ? "270px"
    : "200px";

  // If not full screen, we just render the form
  return !isFullScreen ? (
    <div className={classNames("pt-6", className)}>
      <CoordinatorSimulationBanner simulation={chatSession?.simulation} />
      <AskAIForm
        onSubmit={handleSubmitMessage}
        chatSessionId={chatSessionId}
        inputMessage={inputMessage}
        attachments={attachments}
        loadingCreateMessage={loadingCreateMessage}
        onClickChatSessionId={onChangeChatSessionId}
        showChatHistory={showChatHistory}
        loadingRetryAskAiMessage={loadingRetryAskAiMessage}
        onChangeInputValue={handleChangeInputValue}
        onChangeAttachments={setAttachments}
        autoFocus={false}
        onEditorReady={handleEditorReady}
        isIncognito={isIncognito}
        showStopButton={showStopButton}
        onStopResponse={handleStopResponse}
        collapsibleInput={collapsibleInput}
      />
      {showPromptSuggestions && !chatSessionId && (
        <AskAIPromptSuggestions
          onClickSuggestion={handleClickPromptSuggestion}
        />
      )}
    </div>
  ) : (
    <div className={classNames("flex-1 flex flex-col", className)}>
      <CoordinatorSimulationBanner simulation={chatSession?.simulation} />
      <div className="flex-1 relative">
        {waitingForSuggestedAssessmentResponse ? (
          <div className="pt-6">
            <AskAILoading
              initialLabel="Generating suggested responses..."
              key={retryCount}
              lastUpdate={lastUpdate}
              onClickRetry={handleRetryLastMessage}
              showStaleRetryCTA={!isActivelyWaitingForResponse}
            />
          </div>
        ) : hasNoMessages && !loading ? (
          <div className="flex flex-col h-full">
            {isIncognito ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                <div className="tf-header-2xl font-semibold text-gray-900">
                  Temporary Chat
                </div>
                <div className="mt-3 max-w-md text-sm leading-5 text-gray-500">
                  This conversation won't be saved to your history or remembered
                  by Topicflow AI.
                </div>
              </div>
            ) : (
              <div className="flex-1" />
            )}
            {assessmentId ? (
              <div className="text-gray-800 pt-6">
                <div className="text-base leading-4 mb-3 font-medium">
                  It looks like you're completing a {label("review")}!
                </div>
                <div className="text-sm leading-5 mb-10">
                  Click the context button to get guidance on a{" "}
                  {label("review")} question. We use data from Topicflow and
                  other integrations as sources, only visible to the responder.
                  When delivered, viewers will only see sources they have access
                  to.
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div
            className={classNames(
              "absolute top-0 bottom-0 left-0 overflow-y-auto flex flex-col",
              isInSidebar ? "-right-6" : "right-0",
            )}
            ref={scrollRef}
            id="ask-ai-scroll-container"
          >
            <div
              className={classNames(
                "pt-10 pb-8 flex flex-col w-full max-w-[900px] mx-auto",
                isInSidebar ? "gap-4 pr-6" : "gap-4 px-6",
              )}
            >
              {filteredMessages.map((message, index) => (
                <div
                  // don't use key=message.id to otherwise it'll re-render the loading UI.
                  // AskAIMessage has the key=message.id so it'll re-render when the id is changed.
                  key={index}
                  style={{
                    minHeight:
                      hasPostedMessage && index === filteredMessages.length - 1
                        ? `calc(100vh - ${heightOfFormAndLastMessage} - ${totalYPaddingAroundContainer}px)`
                        : undefined,
                  }}
                >
                  <AskAIMessage
                    message={message}
                    key={message.id}
                    chatSessionId={chatSessionId}
                    reaction={reactionsByMessageId[message.id] || null}
                    isLastMessage={message.id === lastMessage?.id}
                    isAssessmentSession={isAssessmentSession}
                    isResponding={isResponding}
                    ref={
                      message.id === lastMessageFromUser?.id
                        ? lastUserMessageRef
                        : undefined
                    }
                  />

                  {message.id === lastMessage?.id && (
                    <div
                      className={classNames(
                        "flex flex-col gap-3 min-h-[40px] empty:hidden",
                        errorCreateMessage ||
                          responseStatus === "loading" ||
                          lastMessageIsError
                          ? "mt-10"
                          : responseStatus === "streaming-cursor"
                            ? "mt-2"
                            : "mt-6",
                      )}
                      id="status-container"
                    >
                      <AnimatePresence initial={false}>
                        {(showLoadingUI || functionCallLabel) &&
                          liveToolCalls.length > 0 && (
                            <AskAIToolCalls
                              key="live-tool-calls"
                              toolCalls={liveToolCalls}
                              isLive={true}
                            />
                          )}
                      </AnimatePresence>
                      {errorCreateMessage ? (
                        <AskAIErrorMessage
                          key={retryCount}
                          onClickRetry={handleReSendMessage}
                        />
                      ) : responseStatus === "loading" ? (
                        <AskAILoading
                          initialLabel={
                            liveToolCalls.length > 0
                              ? "Thinking..."
                              : functionCallLabel || "Thinking..."
                          }
                          key={retryCount}
                          lastUpdate={lastUpdate}
                          onClickRetry={handleRetryLastMessage}
                          showStaleRetryCTA={!isActivelyWaitingForResponse}
                        />
                      ) : lastMessageIsError ? (
                        <Button
                          size={ButtonSize.medium}
                          text="Try again?"
                          onClick={handleRetryLastMessage}
                        />
                      ) : responseStatus === "streaming-cursor" ? (
                        // Keep the in-progress affordance stable after content
                        // starts, even if tool/loading signals flicker.
                        <span
                          className="inline-flex h-5 items-center"
                          aria-label="Generating response"
                        >
                          <span className="h-4 w-2 animate-blink bg-gray-400" />
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="relative shrink-0 h-px">
        {hasMessages && canClickScrollToBottom && (
          <button
            type="button"
            onClick={handleClickScrollToBottom}
            className="bg-white absolute left-1/2 -translate-x-1/2 -top-10 rounded-full p-1.5 text-gray-600 border hover:bg-gray-100"
            aria-label="Scroll to bottom"
          >
            <TopicflowIcon type="downArrow" className="h-4 w-4" />
          </button>
        )}
      </div>
      <div
        className={classNames(
          "w-full max-w-[900px] mx-auto",
          !isInSidebar && "px-6 pb-6",
        )}
      >
        <AskAIForm
          onSubmit={handleSubmitMessage}
          chatSessionId={chatSessionId}
          inputMessage={inputMessage}
          attachments={attachments}
          loadingCreateMessage={loadingCreateMessage}
          loadingRetryAskAiMessage={loadingRetryAskAiMessage}
          onChangeInputValue={handleChangeInputValue}
          onChangeAttachments={setAttachments}
          onEditorReady={handleEditorReady}
          autoFocus={true}
          onClickChatSessionId={onChangeChatSessionId}
          showChatHistory={showChatHistory}
          isIncognito={isIncognito}
          showStopButton={showStopButton}
          onStopResponse={handleStopResponse}
        />
      </div>
    </div>
  );
};

export default AskAI;
